import React, { useMemo, useEffect, useRef, useState } from 'react';

import {
  RocketOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  HomeOutlined,
  ShopOutlined,
  InboxOutlined,
  ShoppingCartOutlined,
  SettingOutlined,
  SmileOutlined,
  AppstoreOutlined,
  TruckOutlined,
  UserAddOutlined,
  AuditOutlined,
  RollbackOutlined,
  ToolOutlined,
  FileAddOutlined,
  ContainerOutlined,
  ScanOutlined,
  UploadOutlined,
  InfoCircleOutlined,
  MenuFoldOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useOnboarding, TUTORIAL_FLOWS } from '@contexts/operator/OnboardingContext';
import { Layout, Button, Space, Card, Typography, Progress, Tooltip, Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const { Sider } = Layout;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const OnboardingDrawer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    showOnboarding,
    selectedTutorial,
    currentStep,
    completeOnboarding,
    skipOnboarding,
    nextStep,
    prevStep,
    goToStep,
    markTaskCompletedAndNext,
    openTutorialSelection,
  } = useOnboarding();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const previousPathRef = useRef(location.pathname);

  // 根據 selectedTutorial 獲取流程定義
  const tutorialFlow = useMemo(() => {
    if (!selectedTutorial || !TUTORIAL_FLOWS[selectedTutorial]) {
      return null;
    }
    return TUTORIAL_FLOWS[selectedTutorial];
  }, [selectedTutorial]);

  // 步驟圖標映射
  const stepIcons = {
    welcome: SmileOutlined,
    complete: CheckCircleOutlined,
    // warehouse_setup
    create_shelf: AppstoreOutlined,
    create_inventory_type: ContainerOutlined,
    create_shipping_account: TruckOutlined,
    // merchant_setup
    create_merchant: ShopOutlined,
    create_product: FileAddOutlined,
    create_sales_channel: ShoppingCartOutlined,
    setup_merchant_inventory_types: ContainerOutlined,
    // inbound_process
    create_inbound: InboxOutlined,
    acceptance: ScanOutlined,
    putaway: UploadOutlined,
    // outbound_process
    create_order: FileAddOutlined,
    create_picking_list: AuditOutlined,
    fulfillment_qc: ScanOutlined,
    // advanced
    create_operator: UserAddOutlined,
    stock_taking: AuditOutlined,
    return_order: RollbackOutlined,
    work_order: ToolOutlined,
  };

  // 建立步驟資料
  const tutorialSteps = useMemo(() => {
    if (!tutorialFlow) return [];

    return tutorialFlow.steps.map((step) => ({
      ...step,
      title: t(`operator.onboarding.${selectedTutorial}.steps.${step.key}.title`),
      icon: stepIcons[step.key] || RocketOutlined,
      component: (
        <StepContent
          t={t}
          tutorialKey={selectedTutorial}
          stepKey={step.key}
        />
      ),
    }));
  }, [tutorialFlow, selectedTutorial, t]);

  const progress = tutorialSteps.length > 0
    ? Math.round(((currentStep + 1) / tutorialSteps.length) * 100)
    : 0;

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      nextStep();
      const nextStepData = tutorialSteps[currentStep + 1];
      if (nextStepData?.navigateTo) {
        navigate(nextStepData.navigateTo);
      }
    } else {
      completeOnboarding();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      prevStep();
      const prevStepData = tutorialSteps[currentStep - 1];
      if (prevStepData?.navigateTo) {
        navigate(prevStepData.navigateTo);
      }
    }
  };

  const handleSkip = () => {
    skipOnboarding();
  };

  // 自動進度追蹤（當用戶完成操作時自動前進到下一步）
  useEffect(() => {
    if (!showOnboarding || tutorialSteps.length === 0) return;

    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;
    const currentStepData = tutorialSteps[currentStep];

    // 檢測從表單頁到成功頁的跳轉，自動前進
    if (
      currentStepData?.formPath &&
      currentStepData?.successPattern &&
      previousPath === currentStepData.formPath &&
      currentStepData.successPattern.test(currentPath)
    ) {
      setTimeout(() => {
        markTaskCompletedAndNext(`${selectedTutorial}_${currentStepData.key}`);
      }, 500);
    }

    previousPathRef.current = currentPath;
  }, [location.pathname, showOnboarding, currentStep, tutorialSteps, markTaskCompletedAndNext, selectedTutorial]);

  if (!selectedTutorial || tutorialSteps.length === 0) {
    return null;
  }

  if (!showOnboarding) {
    return null;
  }

  const currentStepData = tutorialSteps[currentStep];

  // 獲取教學圖標
  const getTutorialIcon = () => {
    const icons = {
      warehouse_setup: <HomeOutlined style={{ fontSize: 20, color: '#1890ff' }} />,
      merchant_setup: <ShopOutlined style={{ fontSize: 20, color: '#52c41a' }} />,
      inbound_process: <InboxOutlined style={{ fontSize: 20, color: '#faad14' }} />,
      outbound_process: <ShoppingCartOutlined style={{ fontSize: 20, color: '#722ed1' }} />,
      advanced: <SettingOutlined style={{ fontSize: 20, color: '#13c2c2' }} />,
    };
    return icons[selectedTutorial] || <RocketOutlined style={{ fontSize: 20, color: '#1890ff' }} />;
  };

  // 折疊狀態：顯示浮動按鈕
  if (isCollapsed) {
    return (
      <Tooltip title={t('operator.onboarding.expand_tutorial')} placement="left">
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<RocketOutlined />}
          onClick={() => setIsCollapsed(false)}
          style={{
            position: 'fixed',
            right: 24,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1001,
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Sider
      width={400}
      style={{
        height: '100vh',
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        overflow: 'auto',
        background: '#fff',
        boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '12px 20px 0',
        background: '#fff',
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
          <Space>
            {getTutorialIcon()}
            <Text strong style={{ fontSize: 16 }}>
              {t(`operator.onboarding.tutorials.${selectedTutorial}.title`)}
            </Text>
          </Space>
          <Space size={4}>
            <Tooltip title={t('operator.onboarding.collapse_tutorial')}>
              <Button
                type="text"
                icon={<MenuFoldOutlined />}
                onClick={() => setIsCollapsed(true)}
                size="small"
              />
            </Tooltip>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={handleSkip}
              size="small"
            />
          </Space>
        </Space>

        <Progress
          percent={progress}
          size="small"
          showInfo={false}
          strokeColor="#4a8db8"
          style={{ marginBottom: 16 }}
        />

        {/* 步驟指示器 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: 12,
          gap: 8,
        }}>
          {tutorialSteps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === currentStep;
            const isPast = index < currentStep;

            return (
              <Tooltip key={step.key} title={step.title}>
                <div
                  onClick={() => {
                    goToStep(index);
                    if (step.navigateTo) {
                      navigate(step.navigateTo);
                    }
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 8,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isActive ? '#4a8db8' : isPast ? '#5a9a62' : '#f0f0f0',
                    marginBottom: 4,
                    border: isActive ? '2px solid #4a8db8' : 'none',
                    boxShadow: isActive ? '0 2px 8px rgba(91, 143, 168, 0.3)' : 'none',
                  }}>
                    <StepIcon style={{
                      fontSize: 16,
                      color: isActive || isPast ? '#fff' : '#999',
                    }} />
                  </div>
                  {isActive && (
                    <Text
                      style={{
                        fontSize: 11,
                        color: '#4a8db8',
                        textAlign: 'center',
                        fontWeight: 600,
                        maxWidth: 70,
                        lineHeight: 1.2,
                      }}
                      ellipsis
                    >
                      {step.title}
                    </Text>
                  )}
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>

      <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
        {currentStepData?.component}
      </div>

      <div style={{
        padding: '12px 20px',
        borderTop: '1px solid #f0f0f0',
        background: '#fff',
        position: 'sticky',
        bottom: 0,
      }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            {currentStep > 0 && (
              <Button onClick={handlePrev}>
                {t('common.button.previous')}
              </Button>
            )}
            {currentStep === tutorialSteps.length - 1 && (
              <Button
                icon={<span style={{ marginRight: 4 }}>📚</span>}
                onClick={() => {
                  skipOnboarding();
                  openTutorialSelection();
                }}
              >
                {t('operator.onboarding.common.view_tutorials')}
              </Button>
            )}
          </Space>
          <Button
            type="primary"
            onClick={handleNext}
            icon={<ArrowRightOutlined />}
            style={currentStep === tutorialSteps.length - 1 ? {
              background: '#16a34a',
              borderColor: '#16a34a',
            } : {}}
          >
            {currentStep < tutorialSteps.length - 1
              ? t('common.button.next')
              : t('common.button.finish')}
          </Button>
        </Space>
      </div>
    </Sider>
  );
};

// Menu path i18n keys for each step
const STEP_MENU_PATHS = {
  create_shelf: ['operator.menu.stocks.title', 'operator.menu.stocks.shelves'],
  create_inventory_type: ['operator.menu.data.title', 'operator.menu.data.inventory_types'],
  create_shipping_account: ['operator.menu.system_settings.title', 'operator.menu.system_settings.shipping_accounts'],
  create_merchant: ['operator.menu.merchants_billings.title', 'operator.menu.merchants_billings.merchants'],
  create_product: ['operator.menu.products.title', 'operator.menu.products.list'],
  create_sales_channel: ['operator.menu.data.title', 'operator.menu.data.sales_channels'],
  setup_merchant_inventory_types: ['operator.menu.data.title', 'operator.menu.data.merchant_inventory_types'],
  create_inbound: ['operator.menu.inbounds.title', 'operator.menu.inbounds.list'],
  acceptance: ['operator.menu.operations.title', 'operator.menu.operations.inbound_acceptance'],
  create_order: ['operator.menu.orders.title', 'operator.menu.orders.list'],
  create_picking_list: ['operator.menu.picking_stock_taking.title', 'operator.menu.picking_lists.generate'],
  fulfillment_qc: ['operator.menu.operations.title', 'operator.menu.operations.fulfillment_qc'],
  create_operator: ['operator.menu.system_settings.title', 'operator.menu.system_settings.operators'],
  stock_taking: ['operator.menu.picking_stock_taking.title', 'operator.menu.stock_takings.title'],
  return_order: ['operator.menu.return_orders.title', 'operator.menu.return_orders.list'],
  work_order: ['operator.menu.production.title', 'operator.menu.production.work_orders.title'],
};

// 步驟內容組件
const StepContent = ({ t, tutorialKey, stepKey }) => {
  // Welcome and Complete have unique layouts
  if (stepKey === 'welcome') {
    return (
      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 56,
              height: 56,
              background: '#dbeafe',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
            }}>
              <RocketOutlined style={{ fontSize: 24, color: '#3b82f6' }} />
            </div>
            <Title level={4} style={{ marginBottom: 4, fontSize: 18 }}>
              {t(`operator.onboarding.${tutorialKey}.steps.welcome.greeting`)}
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t(`operator.onboarding.${tutorialKey}.steps.welcome.description`)}
            </Text>
          </div>

          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {t(`operator.onboarding.${tutorialKey}.steps.welcome.items`, { returnObjects: true })?.map?.((item, index) => {
              const parts = item.split(' - ');
              const title = parts[0];
              const description = parts[1] || '';
              return (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 10,
                  background: '#f9fafb',
                  borderRadius: 8,
                }}>
                  <div style={{
                    width: 28,
                    height: 28,
                    background: '#3b82f6',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{index + 1}</span>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 2, fontSize: 14 }}>{title}</div>
                    {description && <div style={{ fontSize: 13, color: '#6b7280' }}>{description}</div>}
                  </div>
                </div>
              );
            }) || null}
          </Space>

          {t(`operator.onboarding.${tutorialKey}.steps.welcome.time_estimate`) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              background: '#eff6ff',
              borderRadius: 8,
            }}>
              <InfoCircleOutlined style={{ color: '#3b82f6', fontSize: 16 }} />
              <Text style={{ fontSize: 13, color: '#4b5563' }}>
                {t(`operator.onboarding.${tutorialKey}.steps.welcome.time_estimate`)}
              </Text>
            </div>
          )}
        </Space>
      </Card>
    );
  }

  if (stepKey === 'complete') {
    return (
      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}>
        <div style={{
          background: 'linear-gradient(to bottom, #f0fdf4, rgba(220, 252, 231, 0.5))',
          padding: '32px 24px 56px',
          textAlign: 'center',
        }}>
          <div style={{
            width: 72,
            height: 72,
            background: 'linear-gradient(to bottom right, #4ade80, #10b981)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 20px 25px -5px rgba(134, 239, 172, 0.5), 0 10px 10px -5px rgba(134, 239, 172, 0.3)',
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
            {t('operator.onboarding.common.setup_complete')}
          </div>
        </div>

        <div style={{ padding: '0 20px 20px', marginTop: -32 }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            border: '1px solid #f3f4f6',
            padding: 16,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.6 }}>
              {t('operator.onboarding.common.congratulations')}<span style={{ fontWeight: 600, color: '#16a34a' }}>{t(`operator.onboarding.tutorials.${tutorialKey}.title`)}</span>
              <br />
              {tutorialKey === 'advanced' ? t('operator.onboarding.common.review_tip') : t('operator.onboarding.common.next_tutorial')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Generic rendering for all action steps
  const stepPrefix = `operator.onboarding.${tutorialKey}.steps.${stepKey}`;
  const menuPathKeys = STEP_MENU_PATHS[stepKey];
  const guide = t(`${stepPrefix}.guide`, { returnObjects: true });
  const isPutaway = stepKey === 'putaway';

  // Build numbered guide steps from i18n (guide.step1, guide.step2, ...)
  const guideSteps = [];
  if (guide && typeof guide === 'object' && !isPutaway) {
    for (let i = 1; guide[`step${i}`]; i++) {
      guideSteps.push({
        text: guide[`step${i}`],
        desc: guide[`step${i}_desc`] || null,
      });
    }
  }

  const aboutTitle = t(`${stepPrefix}.about_title`, { defaultValue: '' });

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Menu path breadcrumb */}
        {menuPathKeys && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>📍 {t('onboarding.paths.label')}:</span>
            <span style={{
              background: '#f5f5f5',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 13,
            }}>
              <span style={{ color: '#6b7280' }}>{t(menuPathKeys[0])} › </span>
              <span style={{ color: '#3b82f6', fontWeight: 500 }}>{t(menuPathKeys[1])}</span>
            </span>
          </div>
        )}

        {/* Heading and subtitle */}
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>
            {isPutaway ? t(`${stepPrefix}.guide.heading`) : t(`${stepPrefix}.heading`)}
          </div>
          {guideSteps.length > 0 && (
            <div style={{ fontSize: 14, color: '#6b7280' }}>
              {t('operator.onboarding.common.follow_steps')}
            </div>
          )}
          {isPutaway && (
            <div style={{ fontSize: 14, color: '#6b7280' }}>
              {t(`${stepPrefix}.guide.description`)}
            </div>
          )}
        </div>

        {/* Numbered guide steps */}
        {guideSteps.length > 0 && (
          <div style={{
            background: '#f9fafb',
            borderRadius: 12,
            padding: 12,
          }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {guideSteps.map((step, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{
                    width: 20,
                    height: 20,
                    background: '#3b82f6',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 2,
                  }}>
                    <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{index + 1}</span>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 14 }}>{step.text}</div>
                    {step.desc && <div style={{ fontSize: 13, color: '#6b7280' }}>{step.desc}</div>}
                  </div>
                </div>
              ))}
            </Space>
          </div>
        )}

        {/* About section (Collapse) */}
        {aboutTitle && (
          <Collapse ghost expandIconPosition="end">
            <Panel
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <QuestionCircleOutlined style={{ fontSize: 16, color: '#3b82f6' }} />
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>{aboutTitle}</span>
                </div>
              }
              key="1"
              style={{ background: '#eff6ff', borderRadius: 8, border: '1px solid #dbeafe' }}
            >
              <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                {t(`${stepPrefix}.about_description`)}
              </div>
            </Panel>
          </Collapse>
        )}
      </Space>
    </Card>
  );
};

export default OnboardingDrawer;